import { get, post, put, patch, del, getBlob, postForm } from './client'
import type {
  DashboardData,
  MonthData,
  CategoryInsightsData,
  MerchantsResponse,
  TagsResponse,
  SearchResponse,
  SuggestionResult,
  TransactionDetail,
  TransactionSplit,
  Category,
  Tag,
  Business,
  Client,
  BizService,
  Invoice,
  InvoiceLineItem,
  FiscalYearSettings,
  PlaidItem,
  PlaidSyncResult,
  PlaidPreviewResult,
  Receipt, ReceiptUpdatePayload, Transaction,
} from '../types'

// ── Dashboard ──────────────────────────────────────────────────────────────────
export const getDashboard = (page = 1, dateFrom?: string, dateTo?: string, sortField?: string | null, sortDir?: string, isPersonal?: boolean, businessId?: number | null) =>
  get<DashboardData>('/dashboard', { page, ...(dateFrom ? { dateFrom } : {}), ...(dateTo ? { dateTo } : {}), ...(sortField ? { sortField, sortDir: sortDir ?? 'asc' } : {}), ...(isPersonal != null ? { isPersonal: String(isPersonal) } : {}), ...(businessId != null ? { businessId } : {}) })

// ── Transactions ───────────────────────────────────────────────────────────────
export const getAllTransactions = () =>
  get<{ items: DashboardData['items'] }>('/transactions/all')

export const getTransactionFYYears = (isPersonal?: boolean, businessId?: number | null) =>
  get<number[]>('/transactions/fy-years', {
    ...(isPersonal != null ? { isPersonal: String(isPersonal) } : {}),
    ...(businessId  != null ? { businessId }                   : {}),
  })

export const getTransaction = (id: string) =>
  get<TransactionDetail>(`/transactions/${id}`)

export const searchTransactions = (params: {
  q?: string
  // legacy single-value params (kept for backward compatibility)
  field?: string
  value?: string
  subcategory?: string
  tag?: string
  // multi-value chip params (comma-separated, OR within field, AND across fields)
  merchants?: string
  categories?: string
  subcategories?: string
  tags?: string
  dateFrom?: string
  dateTo?: string
  // module filter: true = personal accounts + unlinked; false = business accounts only
  isPersonal?: boolean
  businessId?: number
}) => get<SearchResponse>('/transactions/search', {
  ...params,
  isPersonal: params.isPersonal != null ? String(params.isPersonal) : undefined,
})

export const getSuggestions = (q: string, filters?: {
  merchants?: string; categories?: string; subcategories?: string
  tags?: string; dateFrom?: string; dateTo?: string; isPersonal?: string; businessId?: string
}) => get<SuggestionResult>('/transactions/suggestions', { q, ...filters })

export const getRelatedTransactions = (matchBy: string, value: string, excludeId: string) =>
  get<unknown[]>('/transactions/related', { matchBy, value, excludeId })

export const createTransaction = (body: {
  file?: string | null
  date?: string | null
  description?: string | null
  merchant?: string | null
  merchantDisplayName?: string | null
  charges?: number | null
  categoryId?: number | null
  subCategoryId?: number | null
  tagIds?: number[]
  plaidAccountId?: string | null
  businessId?: number | null
}) => post<{ id: string }>('/transactions', body)

export const updateTransaction = (id: string, body: {
  id: string
  file?: string | null
  date?: string | null
  description?: string | null
  merchant?: string | null
  merchantDisplayName?: string | null
  charges?: number | null
  categoryId?: number | null
  subCategoryId?: number | null
  tagIds?: number[]
  applyToOthers?: boolean
  applyMatchBy?: string
  applyCategory?: boolean
  applyCategoryMode?: string
  applyTags?: boolean
  applyTagsMode?: string
  applyMerchant?: boolean
  applyBusiness?: boolean
  applyBusinessId?: number | null
  primaryLineBusinessId?: number | null
}) => put<void>(`/transactions/${id}`, body)

// ── Month ──────────────────────────────────────────────────────────────────────
export const getMonthData = (year: number, month: number) =>
  get<MonthData>('/month', { year, month })

// ── Categories ─────────────────────────────────────────────────────────────────
export const getCategoryData = (year?: number, month?: number, all?: boolean, isPersonal?: boolean, businessId?: number | null) =>
  get<CategoryInsightsData>('/categories', { year, month, ...(all ? { all: 'true' } : {}), ...(isPersonal != null ? { isPersonal: String(isPersonal) } : {}), ...(businessId != null ? { businessId } : {}) })

// ── Merchants ──────────────────────────────────────────────────────────────────
export const getMerchantData = (year?: number, month?: number, all?: boolean, isPersonal?: boolean, businessId?: number | null) =>
  get<MerchantsResponse>('/merchants', { year, month, ...(all ? { all: 'true' } : {}), ...(isPersonal != null ? { isPersonal: String(isPersonal) } : {}), ...(businessId != null ? { businessId } : {}) })

// ── Tags ───────────────────────────────────────────────────────────────────────
export const getTagData = (year?: number, month?: number, all?: boolean, isPersonal?: boolean, businessId?: number | null) =>
  get<TagsResponse>('/tags/data', { year, month, ...(all ? { all: 'true' } : {}), ...(isPersonal != null ? { isPersonal: String(isPersonal) } : {}), ...(businessId != null ? { businessId } : {}) })

// ── Settings ───────────────────────────────────────────────────────────────────
export const getSettings = (businessId?: number | null) =>
  get<{ categories: Category[]; tags: Tag[] }>('/settings', businessId != null ? { businessId } : undefined)

export const getSubCategories = (categoryId: number) =>
  get<{ id: number; name: string }[]>(`/settings/subcategories/${categoryId}`)

export const getAllTags = () =>
  get<Tag[]>('/settings/tags')

// Categories CRUD
export const addCategory    = (name: string, businessId?: number | null) => post<Category>('/settings/categories', { name, ...(businessId != null ? { businessId } : {}) })
export const renameCategory = (id: number, name: string) => put<void>(`/settings/categories/${id}`, { name })
export const deleteCategory = (id: number) => del<void>(`/settings/categories/${id}`)

// SubCategories CRUD
export const addSubCategory    = (categoryId: number, name: string) => post<{ id: number; name: string; categoryId: number }>(`/settings/categories/${categoryId}/subcategories`, { name })
export const renameSubCategory = (id: number, name: string) => put<void>(`/settings/subcategories/${id}`, { name })
export const deleteSubCategory = (id: number) => del<void>(`/settings/subcategories/${id}`)

// Tags CRUD
export const addTag    = (name: string, businessId?: number | null) => post<Tag>('/settings/tags', { name, ...(businessId != null ? { businessId } : {}) })
export const renameTag = (id: number, name: string) => put<void>(`/settings/tags/${id}`, { name })
export const deleteTag = (id: number) => del<void>(`/settings/tags/${id}`)

// ── Business ───────────────────────────────────────────────────────────────────
export const getBusinesses    = ()                        => get<Business[]>('/business')
export const getBusiness      = (id: number)              => get<Business>(`/business/${id}`)
export const createBusiness   = (body: Partial<Business>) => post<Business>('/business', body)
export const updateBusiness   = (id: number, body: Business) => put<void>(`/business/${id}`, body)
export const deleteBusiness   = (id: number)                 => del<void>(`/business/${id}`)

// ── Clients ────────────────────────────────────────────────────────────────────
export const getClients    = (businessId: number)                     => get<Client[]>(`/business/${businessId}/clients`)
export const getClient     = (id: number)                             => get<Client>(`/business/clients/${id}`)
export const createClient  = (businessId: number, body: Partial<Client>) => post<Client>(`/business/${businessId}/clients`, body)
export const updateClient  = (id: number, body: Client)               => put<void>(`/business/clients/${id}`, body)
export const deleteClient  = (id: number)                             => del<void>(`/business/clients/${id}`)

// ── BizServices ────────────────────────────────────────────────────────────────
export const getBizServices   = (businessId: number, includeInactive = false) =>
  get<BizService[]>(`/business/${businessId}/services`, includeInactive ? { includeInactive: 'true' } : {})
export const getBizService    = (id: number)                              => get<BizService>(`/business/services/${id}`)
export const createBizService = (businessId: number, body: Partial<BizService>) => post<BizService>(`/business/${businessId}/services`, body)
export const updateBizService = (id: number, body: BizService)            => put<void>(`/business/services/${id}`, body)
export const deleteBizService = (id: number)                              => del<void>(`/business/services/${id}`)

// ── Invoices ───────────────────────────────────────────────────────────────────
export const getInvoices      = (businessId: number, status?: string) =>
  get<Invoice[]>('/invoices', { businessId, ...(status ? { status } : {}) })
export const getInvoice       = (id: number)                          => get<Invoice>(`/invoices/${id}`)
export const getNextSequence  = (clientId: number)                    => get<{ sequence: number }>('/invoices/next-sequence', { clientId })
export const createInvoice    = (body: { invoice: Partial<Invoice>; lineItems: Partial<InvoiceLineItem>[] }) =>
  post<Invoice>('/invoices', body)
export const updateInvoice    = (id: number, body: { invoice: Invoice; lineItems: InvoiceLineItem[] }) =>
  put<void>(`/invoices/${id}`, body)
export const deleteInvoice    = (id: number)                          => del<void>(`/invoices/${id}`)
export const approveInvoice      = (id: number) => post<void>(`/invoices/${id}/approve`, {})
export const markSentInvoice     = (id: number) => post<void>(`/invoices/${id}/mark-sent`, {})
export const logPayment          = (id: number, transactionId?: string | null) =>
  post<void>(`/invoices/${id}/log-payment`, transactionId ? { transactionId } : {})
export const downloadInvoicePdf  = (id: number) => getBlob(`/invoices/${id}/pdf`)

// ── Transaction Splits ─────────────────────────────────────────────────────────
export const getSplits = (id: string) =>
  get<TransactionSplit[]>(`/transactions/${id}/split`)

export const setSplits = (id: string, splits: {
  amount: number
  businessId?: number | null
  categoryId?: number | null
  subCategoryId?: number | null
  note?: string | null
  tagIds?: number[]
}[]) => post<TransactionSplit[]>(`/transactions/${id}/split`, { splits })

export const revertSplit = (id: string) =>
  del<void>(`/transactions/${id}/split`)

// ── Global Settings ────────────────────────────────────────────────────────────
export const getFiscalYearSettings    = ()                          => get<FiscalYearSettings>('/settings/fiscal-year')
export const updateFiscalYearSettings = (body: FiscalYearSettings) => put<void>('/settings/fiscal-year', body)

// ── Plaid ──────────────────────────────────────────────────────────────────────
export const createLinkToken  = (isPersonal: boolean) =>
  post<{ linkToken: string }>('/plaid/link-token', { isPersonal })

export const exchangePlaidToken = (publicToken: string, isPersonal: boolean) =>
  post<PlaidItem>('/plaid/exchange-token', { publicToken, isPersonal })

export const getPlaidItems = (isPersonal?: boolean, includeStats = false) => {
  const params: Record<string, string> = {}
  if (isPersonal != null) params.isPersonal = String(isPersonal)
  if (includeStats)       params.includeStats = 'true'
  return get<PlaidItem[]>('/plaid/items', params)
}

/** Fetches transactions from Plaid for preview — does NOT save to DB or advance cursor. */
export const previewPlaidTransactions = (itemId: string) =>
  post<PlaidPreviewResult>(`/plaid/items/${itemId}/preview`, {})

/** Saves previewed transactions to DB — re-fetches from Plaid and commits via raw SQL MERGE. */
export const syncPlaidAccount = (itemId: string) =>
  post<PlaidSyncResult>(`/plaid/items/${itemId}/sync`, {})

export const unlinkPlaidAccount = (itemId: string) =>
  del<void>(`/plaid/items/${itemId}`)

/** Resets the stored cursor so the next pull re-fetches all transactions (up to 90 days). */
export const resetPlaidCursor = (itemId: string) =>
  post<void>(`/plaid/items/${itemId}/reset-cursor`, {})

/**
 * Creates a Plaid link token in update mode for an existing item.
 * Use this when the item has ITEM_LOGIN_REQUIRED — open Plaid Link with the
 * returned token so the user can re-authenticate without unlinking the account.
 */
export const createUpdateLinkToken = (itemId: string) =>
  post<{ linkToken: string }>(`/plaid/items/${itemId}/update-link-token`, {})

export const linkExistingTransaction = (txId: string, plaidAccountId: string) =>
  post<{ plaidAccountId: string }>(`/plaid/transactions/${txId}/link-account`, { plaidAccountId })

/** Calls Plaid accounts/balance/get and refreshes stored balances for all accounts in an item. */
export const refreshPlaidBalances = (itemId: string) =>
  post<PlaidItem>(`/plaid/items/${itemId}/refresh-balances`, {})

/** Sets (or clears when nickname is null/empty) the user-editable nickname on a Plaid account. */
export const updateAccountNickname = (accountId: string, nickname: string | null) =>
  patch<{ plaidAccountId: string; nickname: string | null }>(`/plaid/accounts/${accountId}/nickname`, { nickname })

/** Updates the per-account IsPersonal and SyncEnabled settings. */
export const updateAccountSettings = (accountId: string, body: { isPersonal: boolean; syncEnabled: boolean; businessId?: number | null }) =>
  patch<{ plaidAccountId: string; isPersonal: boolean; syncEnabled: boolean; businessId: number | null }>(`/plaid/accounts/${accountId}/settings`, body)

/**
 * Persists a new display order for institutions.
 * Pass all active itemIds in the desired order — the server assigns SortOrder = array index.
 */
export const reorderPlaidItems = (itemIds: string[]) =>
  put<{ reordered: number }>('/plaid/items/reorder', { ids: itemIds })

/**
 * Persists a new display order for accounts within an institution.
 * Pass all plaidAccountIds in the desired order.
 */
export const reorderPlaidAccounts = (itemId: string, accountIds: string[]) =>
  put<{ reordered: number }>(`/plaid/items/${itemId}/accounts/reorder`, { ids: accountIds })

// ── Receipts ──────────────────────────────────────────────────────────────────

export const uploadReceipts = (businessId: number, fiscalYear: number, files: File[]) => {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  return postForm<Receipt[]>(`/receipts/upload?businessId=${businessId}&fiscalYear=${fiscalYear}`, form)
}

/** SQL-free mobile capture: drops the photo to blob + intake queue; processed in the background. */
export const quickUploadReceipts = (files: File[], businessId = 1) => {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  return postForm<{ accepted: string[]; errors: { file: string; error: string }[] }>(
    `/receipts/quick-upload?businessId=${businessId}`, form)
}

export const getReceipts = (businessId: number, status?: string) =>
  get<Receipt[]>('/receipts', { businessId, ...(status ? { status } : {}) })

export const getUntaggedReceipts = (businessId: number) =>
  get<Receipt[]>('/receipts/untagged', { businessId })

export const updateReceipt = (receiptId: string, body: ReceiptUpdatePayload) =>
  put<Receipt>(`/receipts/${receiptId}`, body)

export const matchReceipt = (receiptId: string, transactionId: string) =>
  post<import('../types').Receipt>(`/receipts/${receiptId}/match`, { transactionId })

export const unlinkReceipt = (receiptId: string) =>
  post<import('../types').Receipt>(`/receipts/${receiptId}/unlink`, {})

export const deleteReceipt = (receiptId: string) =>
  del<void>(`/receipts/${receiptId}`)

export const getTransactionsWithoutReceipts = (businessId: number) =>
  get<Transaction[]>('/receipts/transactions-without-receipts', { businessId })
